import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Outlet,
  useOutletContext,
  useLocation,
  useParams,
} from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { League } from "../context/LeagueContext";
import { useRequest } from "../hooks/useRequest";
import { getSeasonsUrl, getLeagueByAbbrUrl, getTeamsUrl } from "../config/api";
import PublicNavbar from "./PublicNavbar";
import Footer from "../Common/Footer";
import useStateWithLocalStorage from "../hooks/useStateWithLocalStorage";
import { buildSeasonOptions } from "./buildSeasonOptions";
import { SeasonOption, GameTypeFilter, DateFilter } from "./types";
import { Season, Team } from "../types/models";
import { PaginatedResponse } from "../types/api";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { leaguePageMetadata } from "./leaguePageMetadata";
import "./LeaguePublic.css";

// --- Types ------------------------------------------------------------------

export interface LeaguePublicOutletContext {
  seasonId: number | null;
  seasonOptions: SeasonOption[];
  gameTypeFilter: GameTypeFilter;
  setGameTypeFilter: (filter: GameTypeFilter) => void;
  dateFilter: DateFilter;
  setDateFilter: (filter: DateFilter) => void;
  currentSeasonGameType: "regular" | "exhibition" | "playoff" | null;
  includeSpares: boolean;
  setIncludeSpares: (include: boolean) => void;
  teamFilter: number | null;
  setTeamFilter: (teamId: number | null) => void;
  teams: Team[];
  /**
   * Adds the record being viewed — a team, player or game — to the page title.
   * Those names are only known once the child route has loaded its data, so the
   * layout owns the title and child routes contribute just this detail. Cleared
   * automatically on navigation.
   */
  setPageDetail: (detail: string | null) => void;
}

export function useLeaguePublicContext(): LeaguePublicOutletContext {
  return useOutletContext<LeaguePublicOutletContext>();
}

// --- Component --------------------------------------------------------------

export default function LeaguePublicLayout() {
  const auth = useAuth();
  const location = useLocation();
  const { abbr } = useParams<{ abbr: string }>();
  const {
    currentLeague,
    setCurrentLeague,
    selectedSeasonId,
    setSelectedSeasonId,
  } = useLeague();

  // Resolve league from URL parameter
  const leagueUrl = abbr ? getLeagueByAbbrUrl(abbr) : undefined;
  const { data: leagueData, error: leagueError } = useRequest<League>(
    leagueUrl,
    auth.token,
    undefined,
    true, // skip league header since we're fetching the league itself
  );

  // Set the league in context when resolved from URL
  useEffect(() => {
    if (!leagueData) return;

    console.log("[LeaguePublicLayout] Checking if league needs update", {
      hasCurrentLeague: !!currentLeague,
      currentLeagueAbbr: currentLeague?.abbr,
      currentLeagueActiveSeasonId: currentLeague?.active_season_id,
      urlAbbr: abbr,
      apiLeagueAbbr: leagueData.abbr,
      apiLeagueActiveSeasonId: leagueData.active_season_id,
    });

    // Always update if we don't have a league, or if the abbr doesn't match
    if (!currentLeague || currentLeague.abbr !== abbr) {
      console.log(
        "[LeaguePublicLayout] Setting league (new or different abbr)",
      );
      setCurrentLeague(leagueData);
      return;
    }

    // Also update if we have a league with matching abbr but different data
    // This handles cases where domain lookup returned partial data
    if (currentLeague.abbr === abbr && currentLeague.id === leagueData.id) {
      // Check if the data is actually different to avoid unnecessary updates
      const isDifferent =
        currentLeague.updated_at !== leagueData.updated_at ||
        currentLeague.active_season_id !== leagueData.active_season_id;

      console.log(
        "[LeaguePublicLayout] Same league, isDifferent:",
        isDifferent,
      );

      if (isDifferent) {
        console.log("[LeaguePublicLayout] Updating league with fresh data");
        setCurrentLeague(leagueData);
      }
    }
  }, [leagueData, currentLeague, abbr, setCurrentLeague]);

  // Fetch all seasons for this league.
  // Endpoint: GET /leagues/:id/seasons (league-scoped via App-League header)
  const seasonsUrl = currentLeague
    ? getSeasonsUrl(currentLeague.id)
    : undefined;
  const { data: seasonsResponse } = useRequest<PaginatedResponse<Season>>(
    seasonsUrl,
    auth.token,
  );

  const seasons = useMemo(
    () => seasonsResponse?.data ?? [],
    [seasonsResponse?.data],
  );

  // The active season id comes from the league object.
  // Use ?? null to ensure we never get undefined
  const activeSeasonId: number | null = currentLeague?.active_season_id ?? null;

  // Fetch teams for the selected season
  const teamsUrl = selectedSeasonId ? getTeamsUrl(selectedSeasonId) : undefined;
  const { data: teamsResponse } = useRequest<PaginatedResponse<Team>>(
    teamsUrl,
    auth.token,
  );

  const teams = useMemo(() => teamsResponse?.data ?? [], [teamsResponse?.data]);

  const seasonOptions = useMemo(
    () => buildSeasonOptions(seasons, activeSeasonId),
    [seasons, activeSeasonId],
  );

  // Persist filters in localStorage (global, not per-season)
  const [gameTypeFilter, setGameTypeFilter] =
    useStateWithLocalStorage<GameTypeFilter>("lp-gameTypeFilter", "all");
  const [dateFilter, setDateFilter] = useStateWithLocalStorage<DateFilter>(
    "lp-dateFilter",
    "upcoming",
  );
  const [includeSpares, setIncludeSpares] = useStateWithLocalStorage<boolean>(
    "lp-includeSpares",
    false,
  );
  const [teamFilter, setTeamFilter] = useStateWithLocalStorage<number | null>(
    "lp-teamFilter",
    null,
  );
  const [seasonBarCollapsed, setSeasonBarCollapsed] =
    useStateWithLocalStorage<boolean>("lp-seasonBarCollapsed", true);

  // Document title and description. The layout owns these so that a child route
  // and its parent can't overwrite each other's title on mount; record pages
  // contribute their name through setPageDetail instead.
  const [pageDetail, setPageDetail] = useState<string | null>(null);

  // Drop a stale record name as soon as the route changes.
  useEffect(() => {
    setPageDetail(null);
  }, [location.pathname]);

  const pageMetadata = leaguePageMetadata(
    location.pathname,
    currentLeague?.name,
    pageDetail,
  );
  usePageMetadata(pageMetadata?.title, pageMetadata?.description);

  // Validate and clear selectedSeasonId if it no longer exists
  useEffect(() => {
    if (seasons.length === 0) return;
    if (selectedSeasonId === null) return;

    // Check if the selected season still exists in the fetched seasons
    const seasonExists = seasons.some((s) => s.id === selectedSeasonId);
    if (!seasonExists) {
      console.log(
        "[LeaguePublicLayout] Selected season no longer exists, clearing",
        selectedSeasonId,
      );
      setSelectedSeasonId(null);
    }
  }, [seasons, selectedSeasonId, setSelectedSeasonId]);

  // Sync selectedSeasonId when season options load
  useEffect(() => {
    if (seasonOptions.length === 0) return;
    if (selectedSeasonId !== null) return; // Already have a selection

    // Default to the active season or first option
    const defaultOpt =
      seasonOptions.find((o) => o.isDefault) ?? seasonOptions[0];
    if (defaultOpt) {
      setSelectedSeasonId(defaultOpt.seasonId);
    }
  }, [seasonOptions, selectedSeasonId, setSelectedSeasonId]);

  // Reset team filter when season changes (but not on initial load)
  const previousSeasonId = useRef<number | null>(null);
  useEffect(() => {
    if (
      previousSeasonId.current !== null &&
      selectedSeasonId !== null &&
      previousSeasonId.current !== selectedSeasonId
    ) {
      setTeamFilter(null);
    }
    previousSeasonId.current = selectedSeasonId;
  }, [selectedSeasonId, setTeamFilter]);

  // Get the current season's game_type
  const currentSeason = seasons.find((s) => s.id === selectedSeasonId);
  const currentSeasonGameType = currentSeason?.game_type ?? null;

  // Determine which game type filters to show based on the current page
  const basePath = `/leagues/${abbr}`;
  const isStatsPage = location.pathname === `${basePath}/stats`;
  const isSchedulePage = location.pathname === `${basePath}/schedule`;

  const outletContext: LeaguePublicOutletContext = {
    seasonId: selectedSeasonId,
    seasonOptions,
    gameTypeFilter,
    setGameTypeFilter,
    dateFilter,
    setDateFilter,
    currentSeasonGameType,
    includeSpares,
    setIncludeSpares,
    teamFilter,
    setTeamFilter,
    teams,
    setPageDetail,
  };

  const seasonsLoading = !seasonsResponse && currentLeague;

  // Show season bar on standings, schedule, and stats pages only
  const showSeasonBar =
    location.pathname !== basePath &&
    !location.pathname.startsWith(`${basePath}/games/`) &&
    !location.pathname.startsWith(`${basePath}/teams/`) &&
    !location.pathname.startsWith(`${basePath}/players/`);

  const showGameTypeFilter = showSeasonBar;

  // Show loading state while resolving league
  const leagueLoading = abbr && !leagueData && !leagueError && !currentLeague;
  if (leagueLoading) {
    return (
      <div className="lp-shell">
        <PublicNavbar />
        <main className="lp-body">
          <div className="lp-loading">
            <Spinner animation="border" variant="primary" />
          </div>
        </main>
        <Footer variant="app" />
      </div>
    );
  }

  // Show not found state if league doesn't resolve or there's an error
  if (abbr && (leagueError || (!leagueData && !currentLeague))) {
    return (
      <div className="lp-shell">
        <PublicNavbar />
        <main className="lp-body">
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div
              style={{
                fontSize: "64px",
                color: "var(--border-medium)",
                marginBottom: "24px",
              }}
            >
              <i className="bi bi-exclamation-triangle" />
            </div>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: 600,
                color: "var(--text-primary)",
                marginBottom: "12px",
              }}
            >
              League Not Found
            </h2>
            <p
              style={{
                color: "var(--text-secondary)",
                marginTop: "12px",
                fontSize: "16px",
              }}
            >
              The league "{abbr}" could not be found.
            </p>
            <p
              style={{
                color: "var(--text-secondary)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              Please check the URL or contact your league administrator for
              assistance.
            </p>
          </div>
        </main>
        <Footer variant="app" />
      </div>
    );
  }

  return (
    <div className="lp-shell">
      <PublicNavbar />

      <main className="lp-body">
        {showSeasonBar && (
          <div className="lp-season-bar">
            <div
              className="lp-season-bar-header"
              style={{ display: "flex", alignItems: "center", gap: "16px" }}
            >
              <span className="lp-season-label">
                {seasonsLoading ? "Loading…" : "Season"}
              </span>

              {/* Collapsed view - mobile only */}
              <span
                className="lp-season-viewing"
                style={{ display: seasonBarCollapsed ? undefined : "none" }}
              >
                {seasonsLoading
                  ? ""
                  : seasonOptions.find(
                      (opt) => opt.seasonId === selectedSeasonId,
                    )?.label ?? ""}
              </span>

              {/* Expanded view - always on desktop, conditional on mobile */}
              <div
                className="lp-season-select-wrapper"
                style={{ display: seasonBarCollapsed ? "none" : undefined }}
              >
                {seasonsLoading ? (
                  <Spinner animation="border" size="sm" variant="secondary" />
                ) : (
                  <select
                    className="lp-season-select"
                    value={selectedSeasonId ?? ""}
                    onChange={(e) =>
                      setSelectedSeasonId(Number(e.target.value))
                    }
                    aria-label="Select season"
                  >
                    {seasonOptions.map((opt) => (
                      <option key={opt.seasonId} value={opt.seasonId}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <button
                className="lp-season-bar-toggle"
                onClick={() => setSeasonBarCollapsed(!seasonBarCollapsed)}
                aria-label={
                  seasonBarCollapsed ? "Expand filters" : "Collapse filters"
                }
                aria-expanded={!seasonBarCollapsed}
              >
                <i
                  className={`bi bi-chevron-${
                    seasonBarCollapsed ? "down" : "up"
                  }`}
                />
              </button>
            </div>

            <div
              className={`lp-season-bar-content ${
                seasonBarCollapsed ? "collapsed" : ""
              }`}
            >
              {isStatsPage && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--border-dark)",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={includeSpares}
                    onChange={(e) => setIncludeSpares(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  Include Spares
                </label>
              )}

              {showGameTypeFilter && !seasonsLoading && (
                <div className="lp-filters-wrapper">
                  {isSchedulePage && (
                    <>
                      <select
                        className="lp-season-select"
                        value={teamFilter ?? "all"}
                        onChange={(e) =>
                          setTeamFilter(
                            e.target.value === "all"
                              ? null
                              : Number(e.target.value),
                          )
                        }
                        aria-label="Filter by team"
                        style={{ minWidth: "150px" }}
                      >
                        <option value="all">All Teams</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                      <div
                        className="lp-game-type-filter"
                        role="radiogroup"
                        aria-label="Filter by date"
                      >
                        <button
                          type="button"
                          role="radio"
                          aria-checked={dateFilter === "all"}
                          className={`lp-filter-btn${
                            dateFilter === "all" ? " active" : ""
                          }`}
                          onClick={() => setDateFilter("all")}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={dateFilter === "upcoming"}
                          className={`lp-filter-btn${
                            dateFilter === "upcoming" ? " active" : ""
                          }`}
                          onClick={() => setDateFilter("upcoming")}
                        >
                          Upcoming
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={dateFilter === "past"}
                          className={`lp-filter-btn${
                            dateFilter === "past" ? " active" : ""
                          }`}
                          onClick={() => setDateFilter("past")}
                        >
                          Past
                        </button>
                      </div>
                    </>
                  )}
                  <div
                    className="lp-game-type-filter"
                    role="radiogroup"
                    aria-label="Filter by game type"
                  >
                    {isStatsPage ? (
                      <>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={gameTypeFilter === "regular"}
                          className={`lp-filter-btn${
                            gameTypeFilter === "regular" ? " active" : ""
                          }`}
                          onClick={() => setGameTypeFilter("regular")}
                        >
                          Regular Season
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={gameTypeFilter === "playoff"}
                          className={`lp-filter-btn${
                            gameTypeFilter === "playoff" ? " active" : ""
                          }`}
                          onClick={() => setGameTypeFilter("playoff")}
                        >
                          Playoffs
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={gameTypeFilter === "all"}
                          className={`lp-filter-btn${
                            gameTypeFilter === "all" ? " active" : ""
                          }`}
                          onClick={() => setGameTypeFilter("all")}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={gameTypeFilter === "regular"}
                          className={`lp-filter-btn${
                            gameTypeFilter === "regular" ? " active" : ""
                          }`}
                          onClick={() => setGameTypeFilter("regular")}
                        >
                          Regular Season
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={gameTypeFilter === "playoff"}
                          className={`lp-filter-btn${
                            gameTypeFilter === "playoff" ? " active" : ""
                          }`}
                          onClick={() => setGameTypeFilter("playoff")}
                        >
                          Playoffs
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <Outlet context={outletContext} />
      </main>

      <Footer variant="app" />
    </div>
  );
}
